import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  dismissNotificationAlertsAPI,
  fetchNotificationAlertsAPI,
} from "../service/home";

/**
 * Persistent, server-backed alerts for the loud notification types: a
 * document you sent out was approved, it came back for rework, someone
 * assigned you work, or a task was marked urgent at creation.
 *
 * Deliberately separate from `SnackbarSlice`, which holds ephemeral
 * toasts that vanish after three seconds. These survive a logout: the
 * backend keeps `alert_dismissed_at` per row, so anything that happened
 * while the user was away is waiting for them at next login. The two
 * work together — a live websocket event raises both a toast (noticed
 * now) and an alert card (still there if you looked away).
 *
 * Every card here comes from the server, including on a live event: the
 * middleware refetches rather than synthesizing a local card. An earlier
 * revision did synthesize one, with a negative id standing in for the
 * real notification id the websocket payload does not carry, and that
 * id was the root of three separate defects — it could not be dismissed
 * durably, an in-flight refetch would resurrect a card the user had just
 * cleared, and deduping placeholders against server rows meant comparing
 * fields that were not always present. The backend sends these events
 * after commit, so the row is already readable and one refetch produces
 * a real, dismissable card. The toast covers the instant feedback.
 *
 * The banner cap lives on the server. `results` holds at most three
 * cards; `remainingCount` and `countsByKind` drive the "+N more"
 * roll-up without shipping the whole backlog to the client.
 */

export const ALERT_KIND_LABELS = {
  urgent_task: "urgent task",
  approved: "approved",
  rework_requested: "sent back for rework",
  assigned: "new assignment",
};

// Server notification_type -> the kind key used in countsByKind.
//
// ASSIGNMENT_ASSIGNED, not TASK_ASSIGNED: the server split the two on
// 2026-07-27 so a quick "Assign task" stays in the bell while a real
// assignment earns the banner. TASK_ASSIGNED is deliberately absent --
// it is no longer in the server's alert allowlist, so it never arrives
// here, and listing it would only mislabel it "new assignment".
//
// TASK_URGENT (Matthew, 2026-08-11) is the one carve-out to that: a
// plain task the assigner marked Urgent at creation, distinct from
// TASK_ASSIGNED so it CAN join the allowlist without dragging every
// quiet task in with it.
export const KIND_BY_TYPE = {
  TASK_URGENT: "urgent_task",
  APPROVED: "approved",
  REWORK_REQUESTED: "rework_requested",
  ASSIGNMENT_ASSIGNED: "assigned",
  ASSIGNMENT_COMPLETED: "assigned",
};

export const fetchNotificationAlerts = createAsyncThunk(
  "alerts/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchNotificationAlertsAPI();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load alerts"
      );
    }
  }
);

export const dismissNotificationAlerts = createAsyncThunk(
  "alerts/dismiss",
  async (payload, { rejectWithValue }) => {
    try {
      await dismissNotificationAlertsAPI(
        payload?.all
          ? { all: true }
          : { notification_ids: payload?.notification_ids || [] }
      );
      return payload;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to dismiss alert"
      );
    }
  }
);

const emptyCounts = {
  urgent_task: 0,
  approved: 0,
  rework_requested: 0,
  assigned: 0,
};

const AlertsSlice = createSlice({
  name: "alerts",
  initialState: {
    results: [],
    totalCount: 0,
    remainingCount: 0,
    countsByKind: { ...emptyCounts },
    status: "idle",
    error: null,
  },
  reducers: {
    clearAlerts: (state) => {
      state.results = [];
      state.totalCount = 0;
      state.remainingCount = 0;
      state.countsByKind = { ...emptyCounts };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationAlerts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchNotificationAlerts.fulfilled, (state, action) => {
        const data = action.payload || {};
        state.status = "succeeded";
        state.error = null;
        state.results = data.results || [];
        state.totalCount = data.total_count || 0;
        state.remainingCount = data.remaining_count || 0;
        state.countsByKind = { ...emptyCounts, ...(data.counts_by_kind || {}) };
      })
      .addCase(fetchNotificationAlerts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(dismissNotificationAlerts.pending, (state, action) => {
        // Optimistic: the banner is an interrupt, so it must get out of
        // the way on click rather than after a round trip. A failed
        // dismiss simply reappears on the next fetch.
        const { notification_ids: ids, all } = action.meta.arg || {};
        if (all) {
          state.results = [];
          state.totalCount = 0;
          state.remainingCount = 0;
          state.countsByKind = { ...emptyCounts };
          return;
        }
        const dismissing = new Set(ids || []);
        const kept = [];
        state.results.forEach((row) => {
          if (!dismissing.has(row.id)) {
            kept.push(row);
            return;
          }
          // Decrement the kind too, or the roll-up keeps counting a
          // card the user just cleared and over-reports against
          // remainingCount.
          const kind = row.kind || KIND_BY_TYPE[row.notification_type];
          if (kind && state.countsByKind[kind] > 0) {
            state.countsByKind[kind] -= 1;
          }
        });
        const removed = state.results.length - kept.length;
        state.results = kept;
        state.totalCount = Math.max(state.totalCount - removed, 0);
        state.remainingCount = Math.max(
          state.totalCount - state.results.length,
          0
        );
      });
  },
});

export const { clearAlerts } = AlertsSlice.actions;

export const selectAlerts = (state) => state.alerts;

export default AlertsSlice.reducer;
